import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const getProxyUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  return `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy`;
};

const getHeaders = () => {
  const sessionToken = localStorage.getItem('auth_session_token');
  if (!sessionToken) {
    throw new Error('Niet ingelogd (geen sessie token)');
  }
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  return {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken,
    'apikey': anonKey,
    'Authorization': `Bearer ${anonKey}`,
  };
};

export interface PackerBasePaint {
  product_naam: string;
  eenheid?: string;
  locatie: string;
  voorraad: number;
  match_type?: string;
  is_out_of_stock?: boolean;
  ignored_brand?: boolean;
  booking_product?: {
    product_id: string;
    product_naam: string | null;
    eenheid: string | null;
  } | null;
}

export interface PackerLineItem {
  lineitemindex: number;
  producttitle: string;
  sku?: string | null;
  volume?: string | null;
  color_code?: string | null;
  hex_color?: string | null;
  color_type?: "light" | "dark" | "medium" | null;
  collection_name?: string | null;
  quantity: number;
  image_url?: string | null;
  varianttitle?: string | null;
  base_paint?: PackerBasePaint | null;
  brand?: string | null;
  /** Lokaal verrijkt vanuit inpakkers_orders.customfieldcolor; bevat KLEUR + Waaier (bv "U0.10.70 - Sikkens 4041 | Light | BDCDE6"). */
  customfieldcolor?: string | null;
  /**
   * True wanneer (sku,varianttitle) binnen deze order op 2+ line_items voorkomt
   * EN we geen betrouwbare kleur uit de Decoprof-API hebben. inpakkers_orders kan
   * same-SKU+variant regels collapsen tot 1 rij, dus de lokale customfieldcolor-
   * fallback is dan onbetrouwbaar en wordt niet toegepast. Etiket-render toont
   * dan "KLEUR ONBEKEND - CONTROLEREN".
   */
  colorAmbiguous?: boolean;
}

export interface PackerOrder {
  ordernumber: string;
  orderid: number;
  status: string;
  is_printed: boolean;
  is_future_shipping?: boolean;
  customer: { city?: string };
  item_count: number;
  line_items: PackerLineItem[];
  shippingdate: string | null;
}

export interface PackersListMeta {
  total: number;
  limit: number;
  offset: number;
  packed_today: number;
  recently_cancelled: string[];
  recently_shipped: string[];
}

export interface PackersFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export const usePackersList = (filters: PackersFilters = {}) => {
  const sessionToken = typeof window !== 'undefined' ? localStorage.getItem('auth_session_token') : null;
  return useQuery({
    queryKey: ['packers-list', filters, sessionToken],
    queryFn: async () => {
      const params = new URLSearchParams({ action: 'packers_list' });
      if (filters.search) params.set('search', filters.search);
      params.set('limit', String(filters.limit || 100));
      if (filters.offset) params.set('offset', String(filters.offset));

      const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Fout bij ophalen packers lijst');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      const result = json as { success: boolean; data: PackerOrder[]; meta: PackersListMeta };

      // Enrich missing image_urls from local orders_live_ls
      try {
        const skusWithoutImage = new Set<string>();
        for (const order of result.data) {
          for (const item of order.line_items) {
            if (!item.image_url && item.sku) {
              skusWithoutImage.add(item.sku);
            }
          }
        }
        if (skusWithoutImage.size > 0) {
          // Try SKU-prefix matching: group by prefix (everything before last hyphen)
          const prefixToSkus = new Map<string, string[]>();
          for (const sku of skusWithoutImage) {
            const lastDash = sku.lastIndexOf("-");
            const prefix = lastDash > 0 ? sku.substring(0, lastDash) : sku;
            if (!prefixToSkus.has(prefix)) prefixToSkus.set(prefix, []);
            prefixToSkus.get(prefix)!.push(sku);
          }

          // Query orders_live_ls for any row with matching SKU prefix that has an image
          const allPrefixes = [...prefixToSkus.keys()];
          const imageMap = new Map<string, string>();

          // Query in chunks of 20 prefixes using ilike
          for (let i = 0; i < allPrefixes.length; i += 20) {
            const chunk = allPrefixes.slice(i, i + 20);
            // Use exact SKU match first, then prefix
            const { data: rows } = await supabase
              .from("orders_live_ls")
              .select("sku, image_url")
              .not("image_url", "is", null)
              .not("sku", "is", null)
              .in("sku", [...skusWithoutImage])
              .limit(500);

            if (rows) {
              for (const row of rows) {
                if (row.sku && row.image_url && !imageMap.has(row.sku)) {
                  imageMap.set(row.sku, row.image_url);
                }
              }
            }

            // Also try prefix-based matching for unresolved SKUs
            const unresolvedPrefixes = chunk.filter(p => {
              const skus = prefixToSkus.get(p) || [];
              return skus.some(s => !imageMap.has(s));
            });
            if (unresolvedPrefixes.length > 0) {
              // Build OR filter for prefix matching
              const orFilter = unresolvedPrefixes.map(p => `sku.like.${p}%`).join(",");
              const { data: prefixRows } = await supabase
                .from("orders_live_ls")
                .select("sku, image_url")
                .not("image_url", "is", null)
                .not("sku", "is", null)
                .or(orFilter)
                .limit(500);

              if (prefixRows) {
                for (const row of prefixRows) {
                  if (row.sku && row.image_url) {
                    const lastDash = row.sku.lastIndexOf("-");
                    const prefix = lastDash > 0 ? row.sku.substring(0, lastDash) : row.sku;
                    // Map all SKUs with same prefix to this image
                    const targetSkus = prefixToSkus.get(prefix);
                    if (targetSkus) {
                      for (const s of targetSkus) {
                        if (!imageMap.has(s)) {
                          imageMap.set(s, row.image_url);
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          // Apply images to line items
          if (imageMap.size > 0) {
            for (const order of result.data) {
              for (const item of order.line_items) {
                if (!item.image_url && item.sku && imageMap.has(item.sku)) {
                  item.image_url = imageMap.get(item.sku)!;
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn("[usePackersList] Image enrichment failed:", e);
      }

      // Enrich customfieldcolor from local inpakkers_orders (needed for KLEUR+Waaier on local labels).
      // Uses a SECURITY DEFINER RPC because the frontend Supabase client runs as anon role
      // (custom session via x-session-token header), and inpakkers_orders RLS only allows authenticated.
      try {
        const ordernumbers = result.data.map(o => o.ordernumber).filter(Boolean);
        if (ordernumbers.length > 0) {
          const cfcMap = new Map<string, string>(); // key: `${ordernumber}#${lineitemindex}`
          for (let i = 0; i < ordernumbers.length; i += 50) {
            const chunk = ordernumbers.slice(i, i + 50);
            const { data: rows, error } = await (supabase as any).rpc(
              "get_packer_customfieldcolors",
              { p_ordernumbers: chunk }
            );
            if (error) {
              console.warn("[usePackersList] customfieldcolor rpc error:", error);
              continue;
            }
            if (rows) {
              for (const row of rows as Array<{ ordernumber: string; lineitemindex: number; customfieldcolor: string | null }>) {
                if (row.customfieldcolor) {
                  cfcMap.set(`${row.ordernumber}#${row.lineitemindex}`, row.customfieldcolor);
                }
              }
            }
          }
          // Bepaal per order de set (sku|varianttitle)-keys die op 2+ line_items
          // voorkomen. inpakkers_orders kan zulke rijen samengevoegd hebben tot
          // 1 rij, dus is de customfieldcolor-fallback dan onbetrouwbaar (kan
          // kleur van andere line met dezelfde sku+variant zijn).
          for (const order of result.data) {
            const counts = new Map<string, number>();
            for (const item of order.line_items) {
              const k = `${item.sku ?? ""}|${item.varianttitle ?? ""}`;
              counts.set(k, (counts.get(k) ?? 0) + 1);
            }
            const ambiguousKeys = new Set<string>();
            for (const [k, n] of counts) if (n >= 2) ambiguousKeys.add(k);

            for (const item of order.line_items) {
              const itemKey = `${item.sku ?? ""}|${item.varianttitle ?? ""}`;
              const isAmbiguous = ambiguousKeys.has(itemKey);
              const k = `${order.ordernumber}#${item.lineitemindex}`;
              const cfc = cfcMap.get(k);

              if (!isAmbiguous) {
                // Ongewijzigd gedrag: pas lokale fallback toe wanneer API leeg.
                if (cfc && !item.customfieldcolor) {
                  item.customfieldcolor = cfc;
                }
              } else {
                // Ambigu: NIET mergen van inpakkers_orders (kan collapsed rij zijn).
                // Behoud alleen wat de Decoprof-API zelf leverde.
                if (!item.customfieldcolor) {
                  item.colorAmbiguous = true;
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn("[usePackersList] customfieldcolor enrichment failed:", e);
      }

      return result;
    },
    enabled: !!sessionToken,
    refetchInterval: 30000,
  });
};

export const useMarkPrinted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_printed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij markeren als afgedrukt');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) gemarkeerd als afgedrukt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useMarkPacked = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_packed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij inpakken');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) ingepakt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUnpack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'unpack', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij uitpakken');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) uitgepakt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

// Decode base64 PDF and open in new tab
const downloadBase64Pdf = (base64: string, filename: string) => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Fetch a PDF label for a single order and open it in a new tab
export const fetchLabel = async (ordernumber: string): Promise<void> => {
  const params = new URLSearchParams({ action: 'generate_labels', ordernumber });
  const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Fout bij ophalen label voor ${ordernumber}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Onbekende fout');
  if (!json.data?.pdf_base64) throw new Error('Geen PDF ontvangen');
  downloadBase64Pdf(json.data.pdf_base64, `etiketten-${ordernumber}.pdf`);
};

// Fetch labels for multiple orders in a single request
export const fetchLabelsForOrders = async (ordernumbers: string[]): Promise<void> => {
  const params = new URLSearchParams({
    action: 'generate_labels',
    ordernumbers: ordernumbers.join(','),
  });
  const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Fout bij ophalen labels');
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Onbekende fout');
  if (!json.data?.pdf_base64) throw new Error('Geen PDF ontvangen');
  downloadBase64Pdf(json.data.pdf_base64, `etiketten-${ordernumbers.join('-')}.pdf`);
};

// Module-scope idempotency guard: prevents same set re-firing within 5s window
let lastPrintFingerprint: { key: string; ts: number } | null = null;
const DUPLICATE_SUPPRESSED = 'duplicate-suppressed';

/**
 * Lokale etiket-renderer (eigen jsPDF). Vervangt de Decoprof generate_labels-PDF.
 * De decoprof-orders-proxy edge function en de externe Decoprof-API zijn ONGEWIJZIGD.
 * is_printed wordt nog steeds via `useMarkPrinted` (action: mark_printed) gezet door
 * de callers in Packers.tsx — niets aan side-effects gewijzigd.
 */
export const usePrintLabel = () => {
  return useMutation({
    mutationFn: async (orders: PackerOrder[]) => {
      const ordernumbers = orders.map(o => o.ordernumber);
      const key = [...ordernumbers].sort().join(',');
      const now = Date.now();
      if (lastPrintFingerprint && lastPrintFingerprint.key === key && now - lastPrintFingerprint.ts < 5000) {
        throw new Error(DUPLICATE_SUPPRESSED);
      }
      lastPrintFingerprint = { key, ts: now };

      const { buildLabelsPdfForOrders } = await import('@/services/print/packerLabelAdapter');
      const doc = await buildLabelsPdfForOrders(orders);
      const filename = ordernumbers.length === 1
        ? `etiketten-${ordernumbers[0]}.pdf`
        : `etiketten-${ordernumbers.join('-')}.pdf`;
      doc.save(filename);
      return ordernumbers;
    },
    onSuccess: (ordernumbers) => {
      toast.success(`PDF met ${ordernumbers.length} etiket(ten) gedownload`, {
        description: 'Controleer je printer-instellingen als deze automatisch print.',
        duration: 6000,
      });
    },
    onError: (error: Error) => {
      if (error.message === DUPLICATE_SUPPRESSED) {
        // Silently ignore — same batch was just printed
        return;
      }
      toast.error(error.message);
    },
  });
};
